<template>
	<Spin :spinning="isLoading">
		<div v-if="series.length > 0" class="chart-root">
			<div class="legend">
				<div v-for="color in legendColors" :key="color.color" class="legend-item">
					<div class="legend-color" :style="{ backgroundColor: color.color }" />
					<div class="legend-name">{{ color.label }}</div>
				</div>
			</div>
			<div ref="chartWrapper" class="chart-wrapper">
				<div>
					<div v-for="cell in yAxisCells" :key="cell.key" :class="cell.classes" :title="cell.title">{{ cell.content }}</div>
				</div>
				<div class="cells-and-axis-x-wrapper">
					<div v-for="row in rows" :key="row.key" class="chart-row">
						<div
							v-for="cell in row"
							:key="cell.key"
							:class="cell.classes"
							:style="{ backgroundColor: cell.color || 'white', width: cellWidth }"
							:data-value="cell.value"
							:data-label="cell.label"
							:data-category="cell.category"
							:data-series="cell.series"
							:data-additional-data="cell.additionalData"
						/>
					</div>
					<div>
						<div
							v-for="cell in categoryCells"
							:key="cell.key"
							:class="{ 'chart-category': true, 'chart-category-visible': cell.isVisible }"
							:style="{ width: cellWidth }"
						>
							<div :class="{ 'chart-category-content': true, 'chart-category-hidden': !cell.isVisible }">
								<div class="part-no-br" v-for="part in cell.parts" :key="part">{{ part }}</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div v-if="xAxisLabel" class="x-axis-label">{{ xAxisLabel }}</div>
			<div
				v-if="tooltip.isVisible"
				class="chart-tooltip"
				:class="{ 'chart-tooltip--wrap': tooltipWrap }"
				:style="tooltipContainerStyle"
			>
				<div class="chart-tooltip-header">
					{{ tooltip.category }}
				</div>
				<div class="chart-tooltip-content" :class="{ nobr: !tooltipWrap }">
					<div v-if="tooltipWrap" class="chart-tooltip-row">
						<span class="chart-tooltip-label">{{ tooltip.series }}:</span>
						<span class="chart-tooltip-value"><strong>{{ tooltip.value }}</strong></span>
					</div>
					<template v-else>
						{{ tooltip.series }}: <strong>{{ tooltip.value }}</strong>
					</template>
				</div>
				<div v-if="tooltip.additionalData.length > 0" class="chart-tooltip-content">
					<div
						v-for="item in tooltip.additionalData"
						:key="item.label"
						:class="tooltipWrap ? 'chart-tooltip-row' : 'nobr'"
					>
						<template v-if="tooltipWrap">
							<span class="chart-tooltip-label">{{ item.label }}:</span>
							<span class="chart-tooltip-value"><strong>{{ item.value }}</strong></span>
						</template>
						<template v-else>
							{{ item.label }}: <strong>{{ item.value }}</strong>
						</template>
					</div>
				</div>
				<div v-if="tooltipFooter && tooltip.additionalData.length > 0" class="chart-tooltip-footer">
					{{ tooltipFooter }}
				</div>
			</div>
		</div>
		<CenterLayout v-else width="100%" height="200px">
			<Empty :image="simpleImage">
				<template #description v-if="$slots.description">
					<slot name="description"></slot>
				</template>
			</Empty>
		</CenterLayout>
	</Spin>
</template>

<script>
import { ref, reactive, toRef, computed, onMounted, watch, nextTick } from 'vue';
import { Spin, Empty } from 'ant-design-vue';

import Utils from '@/api/Utils';

import { useEventListener } from '@vueuse/core';

import CenterLayout from '@/components/common/CenterLayout.vue';


const calcRealWidth = elements => {
	let width = 0;
	elements.forEach(element => {
		width += element.offsetWidth;
	});
	return width;
}


const calcMaxHeight = elements => {
	let maxHeight = 0;
	elements.forEach(element => {
		maxHeight = Math.max(maxHeight, element.offsetHeight);
	});
	return maxHeight;
}


const EMPTY_COLOR = '#cccccc';


const getColorAndLabel = (colorSettingsMode, colorSettings, noDataLabel, value) => {
	if (value == null) {
		return {
			color: EMPTY_COLOR,
			label: noDataLabel,
		};
	}
	for (const settings of colorSettings) {
		if ((colorSettingsMode === 'lt' && value < settings.maxValue) || (colorSettingsMode === 'exact' && value === settings.exactValue)) {
			return {
				color: '#' + settings.color,
				label: settings.label,
			};
		}
	}
	return {
		color: EMPTY_COLOR,
		label: noDataLabel,
	};
}


export default {
	components: {
		Spin, Empty, CenterLayout,
	},
	props: {
		isLoading: {
			type: Boolean,
		},
		bordered: {
			type: Boolean,
			default: false,
		},
		series: {
			// формат серии: [{
			//   name: string,
			//   data: [{
			//     x: string,
			//     y: number,
			//     data: optional([{ label: string, value: string }])
			//   }]
			// }]
			// если есть data, то {label: value} будет выведено в tooltip
			type: Array,
		},
		categories: {
			// формат категорий: [string]
			type: Array,
		},
		xAxisLabel: {
			type: String,
			default: '',
			required: false,
		},
		yAxisLabelFormatterFunction: {
			type: Function,
			default: value => value,
			required: false,
		},
		yAxisTooltipFormatterFunction: {
			type: Function,
			default: value => value,
			required: false,
		},
		units: {
			// единица измерения значений выводится в попапе
			type: String,
			default: '',
			required: false,
		},
		colorSettingsMode: {
			// lt - сравнение с maxValue
			// exact - сравнение с exactValue
			type: String,
			default: 'lt',
			required: false,
		},
		colorSettings: {
			// формат цветов в режиме lt: [{ maxValue: number, color: string, label: string }]
			// maxValue - значение, при котором цвет меняется. Проверка строго меньше
			// формат цветов в режиме exact: [{ exactValue: number, color: string, label: string }]
			// exactValue - значение, при котором цвет меняется. Проверка строго равно
			type: Array,
		},
		noDataLabel: {
			type: String,
			default: 'no data',
			required: false,
		},
		showLabelNamesInTooltip: {
			type: Boolean,
			default: false,
			required: false,
		},
		tooltipFooter: {
			type: String,
			default: '',
			required: false,
		},
		tooltipWrap: {
			type: Boolean,
			default: false,
			required: false,
		},
		tooltipMaxWidth: {
			type: [Number, String],
			default: null,
			required: false,
		},
	},
	setup(props) {
		const chartWrapper = ref();
		const seriesRef = toRef(props, 'series');
		const categoriesRef = toRef(props, 'categories');
		const colorSettingsRef = toRef(props, 'colorSettings');
		const colorSettingsModeRef = toRef(props, 'colorSettingsMode');
		const unitsRef = toRef(props, 'units');
		const borderedRef = toRef(props, 'bordered');
		const noDataLabelRef = toRef(props, 'noDataLabel');
		const showLabelNamesInTooltipRef = toRef(props, 'showLabelNamesInTooltip');
		const visibleDivider = ref(1);

		const cellWidth = computed(() => 100 / categoriesRef.value.length + '%');

		const rows = computed(() => {
			const rowsCount = seriesRef.value.length;
			const columnsCount = categoriesRef.value.length;
			const result = [];
			for (let row = 0; row < rowsCount; row++) {
				const rowData = [];
				for (let col = 0; col < columnsCount; col++) {
					const value = seriesRef.value[row].data[col].y;
					const { color, label } = getColorAndLabel(colorSettingsModeRef.value, colorSettingsRef.value, noDataLabelRef.value, value);
					rowData.push({
						key: 'value' + row + '|' + col,
						classes: ['chart-cell', 'chart-value', borderedRef.value ? 'chart-cell-border' : ''],
						color,
						label,
						value,
						category: categoriesRef.value[col],
						series: seriesRef.value[row].name,
						additionalData: JSON.stringify(seriesRef.value[row].data[col].data || []),
					});
				}
				result.push(rowData);
			}
			return result;
		});

		const yAxisCells = computed(() => {
			const cells = [];
			for (let row = 0; row < seriesRef.value.length; row++) {
				cells.push({
					key: 'seriesName' + row,
					classes: ['chart-cell', 'chart-series-name'],
					title: props.yAxisTooltipFormatterFunction(seriesRef.value[row].name),
					content: Utils.trimMountpoint(props.yAxisLabelFormatterFunction(seriesRef.value[row].name)),
				});
			}
			return cells;
		});
		const categoryCells = computed(() => {
			const columnsCount = categoriesRef.value.length;
			const cells = [];
			for (let col = 0; col < columnsCount; col++) {
				cells.push({
					key: 'category' + col,
					parts: categoriesRef.value[col].split(' '),
					isVisible: col % visibleDivider.value === 0,
				});
			}
			return cells;
		});

		const updateCategoryLabels = () => {
			if (chartWrapper.value) {
				const chartCategoryElements = chartWrapper.value.querySelectorAll('.chart-category');
				const chartCategoryContentElements = chartWrapper.value.querySelectorAll('.chart-category-content');
				const wrapperWidth = calcRealWidth(chartCategoryElements);
				const contentWidth = calcRealWidth(chartCategoryContentElements);
				const contentHeight = calcMaxHeight(chartCategoryContentElements);
				chartCategoryElements.forEach(element => {
					element.style.height = contentHeight + 'px';
				});
				visibleDivider.value = Math.floor(contentWidth / wrapperWidth / 0.8) + 1;
			}
		};
		useEventListener(window, 'resize', updateCategoryLabels);
		onMounted(updateCategoryLabels);
		watch(seriesRef, () => {
			nextTick(() => {
				updateCategoryLabels();
			});
		});

		const tooltip = reactive({
			isVisible: false,
			value: null,
			category: null,
			series: null,
			additionalData: [],
			inset: '0px',
		});
		useEventListener(chartWrapper, 'mouseout', () => { tooltip.isVisible = false; });
		useEventListener(chartWrapper, 'mousemove', event => {
			const target = event.target;
			if (!target.classList.contains('chart-value')) {
				tooltip.isVisible = false;
				return;
			}
			tooltip.isVisible = true;
			if (event.target.dataset.value == null) {
				tooltip.value = noDataLabelRef.value;
			} else {
				if (showLabelNamesInTooltipRef.value) {
					tooltip.value = event.target.dataset.label;
				} else {
					tooltip.value = event.target.dataset.value + ' ' + unitsRef.value;
				}
			}
			tooltip.category = event.target.dataset.category;
			tooltip.series = props.yAxisTooltipFormatterFunction(event.target.dataset.series);
			tooltip.additionalData = JSON.parse(event.target.dataset.additionalData || '[]');
			const viewportOffset = target.parentElement.parentElement.parentElement.parentElement.getBoundingClientRect();
			const mouseX = event.clientX - viewportOffset.left;
			const mouseY = event.clientY - viewportOffset.top;
			const swapX = mouseX > viewportOffset.width / 2;
			const swapY = mouseY > viewportOffset.height / 2;
			const offset = 5;
			tooltip.left = swapX ? 'auto' : ((mouseX + offset) + 'px');
			tooltip.right = swapX ? ((viewportOffset.width - mouseX + offset) + 'px') : 'auto';
			tooltip.top = swapY ? 'auto' : ((mouseY + offset) + 'px');
			tooltip.bottom = swapY ? ((viewportOffset.height - mouseY - offset) + 'px') : 'auto';
		});
		const tooltipContainerStyle = computed(() => {
			const style = {
				top: tooltip.top,
				right: tooltip.right,
				bottom: tooltip.bottom,
				left: tooltip.left,
			};
			if (props.tooltipMaxWidth != null) {
				style.maxWidth = typeof props.tooltipMaxWidth === 'number'
					? props.tooltipMaxWidth + 'px'
					: props.tooltipMaxWidth;
			}
			return style;
		});

		const legendColors = computed(() => {
			let colors = [{
				color: EMPTY_COLOR,
				label: noDataLabelRef.value,
			}];
			for (const settings of colorSettingsRef.value) {
				colors.push({
					color: '#' + settings.color,
					label: settings.label,
				});
			}
			return colors;
		});
		return {
			chartWrapper,
			cellWidth,
			rows,
			yAxisCells,
			categoryCells,
			legendColors,
			tooltip,
			tooltipContainerStyle,
			simpleImage: Empty.PRESENTED_IMAGE_SIMPLE,
		};
	}
}
</script>

<style scoped>
.chart-root {
	font-family: Helvetica, Arial, sans-serif;
	font-size: 11px;
	position: relative;
	z-index: 1;
	direction: ltr;
}

.chart-wrapper {
	display: flex;
}

.cells-and-axis-x-wrapper {
	flex: 1;
}

.chart-row {
	line-height: 0;
}

.chart-cell {
	height: 23px;
	border-bottom: 1px solid white;
}

.chart-cell-border {
	border-inline-end: 1px solid white;
}

.chart-series-name {
	display: flex;
	align-items: center;
	justify-content: right;
	padding-inline-end: 5px;
}

.chart-value {
	display: inline-block;
}

.chart-value:hover {
	filter: brightness(120%) saturate(120%);
	opacity: 0.7;
}

.chart-category {
	text-align: center;
	display: inline-block;
	position: relative;
}

.chart-category-visible {
	border-top: 1px solid;
}

.chart-category-content {
	position: absolute;
	top: 3px;
	left: 50%;
	transform: translateX(-50%);
	width: min-content;
}

.chart-category-hidden {
	opacity: 0;
}

.legend {
	display: flex;
	margin: 16px auto;
	width: fit-content;
}

.legend-item {
	display: flex;
	align-items: center;
	margin-inline-end: 16px;
}

.legend-color {
	width: 12px;
	height: 12px;
	margin-inline-end: 4px;
}

.x-axis-label {
	margin: 8px auto 16px;
	text-align: center;
}

.chart-tooltip {
	pointer-events: none;
	position: absolute;
	z-index: 10;
	background-color: white;
	box-shadow: 0px 2px 9px #444;
	border-radius: 5px;
}

.chart-tooltip-header {
	white-space: nowrap;
	background-color: #001529;
	color: white;
	padding: 5px 8px 5px;
	border-radius: 0;
	border-start-start-radius: 5px;
	border-start-end-radius: 5px;
	font-weight: 600;
}

.chart-tooltip-content {
	padding: 8px;
}

.chart-tooltip-footer {
	padding: 0 8px 8px;
	white-space: normal;
	color: #666;
	max-width: 200px;
}

.chart-tooltip--wrap .chart-tooltip-header {
	white-space: normal;
}

.chart-tooltip-row {
	display: flex;
	align-items: flex-start;
	gap: 0 4px;
}

.chart-tooltip-label {
	flex: 0 0 auto;
}

.chart-tooltip-value {
	flex: 1 1 auto;
	min-width: 0;
	white-space: normal;
	word-break: break-word;
}

.part-no-br {
	white-space: nowrap;
}
</style>